import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { DateTime } from "luxon";
import { toast } from "react-toastify";
import Icon from "@mdi/react";
import {
    mdiCalendarCursor,
    mdiTrashCan,
    mdiPlus,
    mdiPencil,
    mdiChevronLeft,
    mdiChevronRight,
    mdiCalendarToday,
    mdiCalendarRemove,
} from "@mdi/js";
import MonthCalendarView from "../../components/MonthCalendarView";
import useAvailabilityContexts from "../../hooks/useAvailabilityContexts";
import EditAvailabilityContextModal from "../../components/EditAvailabilityContextModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import AppHeader from "../../components/AppHeader";
import "./BlockDatesPage.css";
import "../header.css";
import "../Common.css";
import "../Dashboard.css";

const BLOCK_TYPE_LABELS = {
    holiday: "Public Holiday",
    leave: "Annual Leave",
    personal: "Personal Day",
};

/**
 * BlockDatesPage - Manage blocked dates (holidays, vacations, personal time)
 *
 * Sub-portal page for instructor availability management
 *
 * ARCHITECTURAL CHANGE (2025):
 * - Now uses availability_contexts (range-based, fully editable)
 * - Replaces availability_overrides approach (individual day overrides)
 * - No grouping logic needed - contexts are stored as ranges
 * - Dates AND reason are fully editable
 *
 * Features:
 * - 2-month calendar view for selecting date ranges
 * - Add blocked date ranges with reason (holidays, vacations, personal time)
 * - List of existing blocked dates with edit and delete options
 * - Dual calendar/list layout with form controls
 * - Color-coded display (similar to travel periods) for visual distinction
 *
 * API Integration:
 * - Uses V2 Availability Contexts API: POST /api/v2/availability/contexts
 * - organisationId extracted from JWT token (not sent in request body)
 * - Request payload: { startDate, endDate, context, mode, reason }
 */

const BlockDatesPage = () => {
    const navigate = useNavigate();
    const {
        blockedDates,
        selectedRange,
        reason,
        blockType,
        isLoading,
        error,
        successMessage,
        setSelectedRange,
        setReason,
        setBlockType,
        handleBlockDates,
        handleEditContext,
        handleDeleteContext,
    } = useAvailabilityContexts();

    // Edit modal state
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editingGroup, setEditingGroup] = useState(null);

    // Delete blocked date confirmation modal state
    const [showDeleteBlockedDateModal, setShowDeleteBlockedDateModal] = useState(false);
    const [contextToDelete, setContextToDelete] = useState(null);

    // Calendar navigation state
    const [currentStartMonth, setCurrentStartMonth] = useState(DateTime.now());

    // Compute if previous month navigation should be disabled
    const isPrevMonthDisabled = useMemo(() => {
        // Disable if viewing current month or earlier
        return currentStartMonth.startOf("month") <= DateTime.now().startOf("month");
    }, [currentStartMonth]);

    // Show toast notifications for success messages only
    // Errors are shown inline below the reason field
    useEffect(() => {
        if (successMessage) {
            toast.success(successMessage);
        }
    }, [successMessage]);

    // Handle opening edit modal
    const handleEditClick = context => {
        setEditingGroup(context);
        setEditModalOpen(true);
    };

    // Handle saving edited context (dates + reason)
    const handleSaveEdit = async (contextId, updates) => {
        await handleEditContext(contextId, updates);
        setEditModalOpen(false);
        setEditingGroup(null);
    };

    // Calendar navigation handlers
    const handlePreviousMonth = () => {
        const newMonth = currentStartMonth.minus({ months: 1 });

        // Safety check: prevent navigation to past months
        if (newMonth.startOf("month") < DateTime.now().startOf("month")) {
            return; // Block navigation
        }

        setCurrentStartMonth(newMonth);
    };

    const handleNextMonth = () => {
        setCurrentStartMonth(prev => prev.plus({ months: 1 }));
    };

    const handleToday = () => {
        setCurrentStartMonth(DateTime.now());
    };

    // Handle deletion of availability context
    const handleDeleteClick = contextId => {
        setContextToDelete(contextId);
        setShowDeleteBlockedDateModal(true);
    };

    const confirmDeleteBlockedDate = async () => {
        if (contextToDelete) {
            await handleDeleteContext(contextToDelete);
        }
        setShowDeleteBlockedDateModal(false);
        setContextToDelete(null);
    };

    // Convert blocked date ranges to individual date strings with colours for calendar display
    const blockedDateStrings = useMemo(() => {
        const allDates = [];
        blockedDates.forEach(range => {
            let current = DateTime.fromISO(range.startDate);
            const end = DateTime.fromISO(range.endDate);
            while (current <= end) {
                allDates.push({ date: current.toISODate(), color: range.color });
                current = current.plus({ days: 1 });
            }
        });
        return allDates;
    }, [blockedDates]);

    // Convert selectedRange for MonthCalendarView (expects DateTime objects)
    const selectedRangeForCalendar = selectedRange
        ? {
              start: DateTime.fromISO(selectedRange.start),
              end: DateTime.fromISO(selectedRange.end),
          }
        : null;

    // Handle range selection from calendar (receives DateTime objects)
    const handleSelectRange = range => {
        if (range) {
            setSelectedRange({
                start: range.start.toISODate(),
                end: range.end.toISODate(),
            });
        } else {
            setSelectedRange(null);
        }
    };

    return (
        <div className="dashboard-container org-console">
            {/* Standard Navigation Header */}
            <AppHeader />

            {/* Main Content */}
            <div className="dashboard-main">
                <div className="dashboard-content-wrapper blocked-dates-page-wrapper">
                    {/* Sub-Portal Header Card */}
                    <div className="sub-portal-header-card">
                        <div className="sub-portal-header-title-row">
                            <button onClick={() => navigate("/manage-availability")} className="back-nav-btn">
                                ← Manage Availability
                            </button>
                            <h1 className="sub-portal-header-title">Dates Unavailable</h1>
                        </div>
                        <p className="sub-portal-header-subtitle">
                            Block dates for holidays, vacations, and personal time
                        </p>
                    </div>

                    {/* Content Card with Dual Layout */}
                    <div className="content-card manage-blocked-dates-page">
                        <div className="blocked-dates-content">
                            {/* Left: Calendar Section */}
                            <div className="calendar-section">
                                {/* Section Title - Centered */}
                                <h3 className="calendar-section-title">Select Date(s) to Block</h3>

                                {/* Instructions */}
                                <p className="section-hint">
                                    Click a single day (e.g., public holiday) or click start and end dates for a range
                                    (e.g., vacation).
                                    <br />
                                    <strong>Double-click</strong> any selected day to clear the selection.
                                </p>

                                {/* Calendar Navigation Buttons - Stretched across top */}
                                <div className="calendar-nav-buttons">
                                    <button
                                        onClick={handlePreviousMonth}
                                        className="btn-calendar-nav"
                                        disabled={isPrevMonthDisabled}
                                        aria-label="Previous month"
                                        style={isPrevMonthDisabled ? { opacity: 0.5, cursor: "not-allowed" } : {}}>
                                        <Icon path={mdiChevronLeft} size={0.7} />
                                        Prev
                                    </button>
                                    <button
                                        onClick={handleToday}
                                        className="btn-calendar-today"
                                        aria-label="Go to today">
                                        <Icon path={mdiCalendarToday} size={0.6} />
                                        Today
                                    </button>
                                    <button
                                        onClick={handleNextMonth}
                                        className="btn-calendar-nav"
                                        aria-label="Next month">
                                        Next
                                        <Icon path={mdiChevronRight} size={0.7} />
                                    </button>
                                </div>

                                <MonthCalendarView
                                    startMonth={currentStartMonth}
                                    blockedDates={blockedDateStrings}
                                    bookingDates={[]}
                                    selectedRange={selectedRangeForCalendar}
                                    onSelectRange={handleSelectRange}
                                    rangeSelectionEnabled={true}
                                />

                                {/* Selected Range Info */}
                                {selectedRange &&
                                    (() => {
                                        const start = DateTime.fromISO(selectedRange.start);
                                        const end = DateTime.fromISO(selectedRange.end);
                                        const isSingleDay = start.hasSame(end, "day");

                                        return (
                                            <div className="selected-range-info">
                                                <Icon path={mdiCalendarCursor} size={0.8} />
                                                <span>
                                                    {isSingleDay
                                                        ? start.toFormat("MMM d, yyyy")
                                                        : `${start.toFormat("MMM d")} - ${end.toFormat("MMM d, yyyy")}`}
                                                </span>
                                            </div>
                                        );
                                    })()}

                                {/* Type Picker */}
                                <div className="form-group">
                                    <label htmlFor="blockType" className="form-label">
                                        Type
                                    </label>
                                    <select
                                        id="blockType"
                                        className="form-input"
                                        value={blockType}
                                        onChange={e => setBlockType(e.target.value)}
                                        disabled={isLoading}>
                                        <option value="holiday">Public Holiday</option>
                                        <option value="personal">Personal Day</option>
                                        <option value="leave">Annual Leave</option>
                                    </select>
                                </div>

                                {/* Reason Input */}
                                <div className="form-group">
                                    <label htmlFor="reason" className="form-label">
                                        Reason <span className="required">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        id="reason"
                                        className="form-input"
                                        placeholder="e.g., Christmas Holiday, Vacation, Personal"
                                        value={reason}
                                        onChange={e => setReason(e.target.value)}
                                        disabled={isLoading}
                                    />
                                    {error && (
                                        <div className="error-message">
                                            <span className="error-icon">⚠</span>
                                            <span>{error}</span>
                                        </div>
                                    )}
                                </div>

                                {/* Block Dates Button */}
                                <button
                                    className="btn-primary btn-block-dates"
                                    onClick={handleBlockDates}
                                    disabled={isLoading || !selectedRange}>
                                    <Icon path={mdiPlus} size={0.8} />
                                    {selectedRange &&
                                    DateTime.fromISO(selectedRange.start).hasSame(
                                        DateTime.fromISO(selectedRange.end),
                                        "day"
                                    )
                                        ? "Block This Date"
                                        : "Block Selected Dates"}
                                </button>
                            </div>

                            {/* Right: Blocked Dates List */}
                            <div className="blocked-list-section">
                                <div className="section-header">
                                    <h3>Dates you're Unavailable</h3>
                                </div>

                                {isLoading && <div className="loading-indicator">Loading...</div>}

                                {!isLoading && blockedDates.length === 0 && (
                                    <div className="empty-state">
                                        <p>No blocked dates</p>
                                        <p className="empty-hint">
                                            Select date(s) on the calendar and add a reason to block them
                                        </p>
                                    </div>
                                )}

                                {!isLoading && blockedDates.length > 0 && (
                                    <div className="blocked-list">
                                        {blockedDates.map(context => {
                                            const start = DateTime.fromISO(context.startDate);
                                            const end = DateTime.fromISO(context.endDate);
                                            const isSingleDay = start.hasSame(end, "day");

                                            return (
                                                <div key={context.id} className="blocked-list-item">
                                                    {/* LEFT: Icon Section */}
                                                    <div className="blocked-item-icon">
                                                        <Icon
                                                            path={mdiCalendarRemove}
                                                            size={1.5}
                                                            color={context.color}
                                                        />
                                                    </div>

                                                    {/* MIDDLE: Content Section */}
                                                    <div className="blocked-item-content">
                                                        <div className="blocked-item-title">{context.reason}</div>
                                                        <div className="blocked-item-meta-row">
                                                            <div className="blocked-item-reason">
                                                                {isSingleDay
                                                                    ? start.toFormat("MMM d, yyyy")
                                                                    : `${start.toFormat("MMM d")} - ${end.toFormat("MMM d, yyyy")}`}
                                                            </div>
                                                            <div
                                                                className="blocked-item-type-pill"
                                                                style={{
                                                                    background: `${context.color}18`,
                                                                    border: `1.5px solid ${context.color}`,
                                                                    color: context.color,
                                                                }}>
                                                                {BLOCK_TYPE_LABELS[context.context] || context.context}
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* RIGHT: Action Buttons */}
                                                    <div className="blocked-item-actions">
                                                        {/* Edit Button */}
                                                        <button
                                                            onClick={e => {
                                                                e.stopPropagation();
                                                                handleEditClick(context);
                                                            }}
                                                            disabled={isLoading}
                                                            title="Edit blocked dates"
                                                            className="blocked-item-icon-button">
                                                            <Icon
                                                                path={mdiPencil}
                                                                size={0.9}
                                                                color="var(--dl-color-gray-600)"
                                                            />
                                                        </button>

                                                        {/* Delete Button */}
                                                        <button
                                                            onClick={e => {
                                                                e.stopPropagation();
                                                                handleDeleteClick(context.id);
                                                            }}
                                                            disabled={isLoading}
                                                            title="Delete blocked dates"
                                                            className="blocked-item-icon-button blocked-item-delete-btn">
                                                            <Icon
                                                                path={mdiTrashCan}
                                                                size={0.9}
                                                                color="var(--dl-color-gray-600)"
                                                            />
                                                        </button>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Edit Availability Context Modal */}
            <EditAvailabilityContextModal
                isOpen={editModalOpen}
                onClose={() => setEditModalOpen(false)}
                context={editingGroup}
                onSave={handleSaveEdit}
            />

            {/* Delete Blocked Date Confirmation Modal */}
            <ConfirmationModal
                isOpen={showDeleteBlockedDateModal}
                title="Delete Blocked Date?"
                message="Are you sure you want to delete this blocked date? This action cannot be undone."
                confirmText="Delete"
                cancelText="Cancel"
                onConfirm={confirmDeleteBlockedDate}
                onCancel={() => {
                    setShowDeleteBlockedDateModal(false);
                    setContextToDelete(null);
                }}
                variant="danger"
            />
        </div>
    );
};

export default BlockDatesPage;
